import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { XCircle, Phone, Mail, Shield, Clock, CreditCard, AlertTriangle, CheckCircle2, HelpCircle } from "lucide-react";

const SLABS = [
  { window: "15 days or more before travel", refund: "100%", color: "green" },
  { window: "7 - 14 days before travel", refund: "75%", color: "teal" },
  { window: "3 - 6 days before travel", refund: "50%", color: "amber" },
  { window: "1 - 2 days before travel", refund: "25%", color: "orange" },
  { window: "Less than 24 hours / no-show", refund: "0%", color: "red" },
];

const chipColor = {
  green: "bg-green-100 text-green-800 border-green-200",
  teal: "bg-teal-100 text-teal-800 border-teal-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  orange: "bg-orange-100 text-orange-800 border-orange-200",
  red: "bg-red-100 text-red-800 border-red-200",
};

const RefundPolicyPage = () => {
  return (
    <>
      <Header />
      <main>
        <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 to-slate-900/50" />
          <div className="relative max-w-7xl mx-auto px-6 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold tracking-wider uppercase">
              <Shield className="w-3.5 h-3.5" /> Refund & Cancellation Policy
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Fair, transparent, hassle-free
            </h1>
            <p className="mt-4 text-white/85 text-lg max-w-2xl">
              Understand exactly how much you get back — no small print, no surprises.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6 space-y-8">
            {/* Refund slab */}
            <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-xl bg-teal-600 text-white flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Cancellation slabs
                  </h2>
                  <p className="text-sm text-slate-500">Refund percentage depends on when you cancel</p>
                </div>
              </div>
              <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
                <div className="grid grid-cols-[1fr_auto] bg-slate-100 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600">
                  <div>Cancellation window</div>
                  <div>Refund</div>
                </div>
                {SLABS.map((s, i) => (
                  <div
                    key={s.window}
                    className={`grid grid-cols-[1fr_auto] items-center px-5 py-4 text-sm ${i < SLABS.length - 1 ? "border-b border-slate-100" : ""}`}
                  >
                    <div className="text-slate-800 font-medium">{s.window}</div>
                    <div>
                      <span className={`px-3 py-1 rounded-full border text-sm font-bold ${chipColor[s.color]}`}>
                        {s.refund}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product-specific */}
            <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                By booking type
              </h2>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {[
                  { title: "Tour Packages", body: "Follows the standard slabs above. Non-refundable components (visas, non-cancellable flights, permits) are deducted first." },
                  { title: "Bus Bookings", body: "Up to 6 hours before departure → slab applies. Within 6 hours or after → non-refundable." },
                  { title: "Car / Cab Bookings", body: "Up to 4 hours before pickup → slab applies. Within 4 hours or after → driver fee (25%) is retained." },
                  { title: "Train Tickets (IRCTC)", body: "Subject to IRCTC rules. PMR service fee (₹20/ticket) is non-refundable." },
                  { title: "Flight Tickets", body: "Subject to airline fare rules. Convenience fee is non-refundable. Refunds only after airline processes." },
                  { title: "Hotel Reservations", body: "Follows the individual hotel's cancellation policy shown at time of booking." },
                ].map((item) => (
                  <div key={item.title} className="p-4 rounded-lg border border-slate-200 hover:border-teal-300 transition">
                    <div className="font-semibold text-slate-900">{item.title}</div>
                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Refund timelines */}
            <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-orange-500 text-white flex items-center justify-center">
                  <CreditCard className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Refund timelines
                </h2>
              </div>
              <ul className="mt-2 space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" /> <span><strong>UPI / Wallets:</strong> instant to 24 hours</span></li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" /> <span><strong>Debit / Credit Cards:</strong> 5-7 business days</span></li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" /> <span><strong>Net Banking:</strong> 5-7 business days</span></li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 shrink-0" /> <span><strong>Cash / Cheque bookings:</strong> refunded via NEFT within 7-10 days after form submission</span></li>
              </ul>
            </div>

            {/* Exceptions */}
            <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-amber-500 text-white flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Non-refundable cases
                </h2>
              </div>
              <ul className="mt-2 space-y-2 text-sm text-slate-700">
                <li className="flex gap-2"><span className="text-red-500 font-bold">•</span> Cancellation within 24 hours of travel or after departure</li>
                <li className="flex gap-2"><span className="text-red-500 font-bold">•</span> No-show at pickup point / bus terminal / hotel</li>
                <li className="flex gap-2"><span className="text-red-500 font-bold">•</span> Visa fees, non-refundable airline segments, permits & entry tickets</li>
                <li className="flex gap-2"><span className="text-red-500 font-bold">•</span> Bookings cancelled by passenger due to personal reasons on peak dates (Diwali, Christmas, New Year, Holi)</li>
              </ul>
            </div>

            {/* Force majeure */}
            <div className="bg-white p-6 md:p-8 rounded-2xl border border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Force majeure & operator cancellations
              </h2>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                In case of natural calamity, government restriction, curfew, road blockage, or cancellation by the operator (bus/airline/hotel), PMR Travels shall issue a <strong>100% refund</strong> of the paid amount (excluding third-party non-refundable charges) OR provide a credit note valid for 12 months, at the traveller's choice.
              </p>
            </div>

            {/* How to cancel */}
            <div className="bg-gradient-to-r from-teal-700 to-teal-600 text-white p-6 md:p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-3">
                <HelpCircle className="w-6 h-6" />
                <h2 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>How to cancel</h2>
              </div>
              <ol className="mt-2 space-y-2 text-sm text-white/90 list-decimal list-inside">
                <li>Click the button below and enter your Booking ID + registered phone</li>
                <li>Review your refund estimate</li>
                <li>Confirm cancellation — refund is initiated instantly to your original payment method</li>
              </ol>
              <div className="mt-5 flex flex-wrap gap-3">
                <Button asChild className="bg-white text-teal-700 hover:bg-slate-100 h-11">
                  <Link to="/cancel"><XCircle className="w-4 h-4 mr-2" /> Cancel a Booking</Link>
                </Button>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-teal-700 bg-white/10 backdrop-blur h-11">
                  <a href="tel:+919397363140"><Phone className="w-4 h-4 mr-2" /> Call +91 9397363140</a>
                </Button>
                <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-teal-700 bg-white/10 backdrop-blur h-11">
                  <a href="mailto:bookings@pmrtravels.com"><Mail className="w-4 h-4 mr-2" /> Email Support</a>
                </Button>
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center">
              This policy is effective from 1 July 2025. PMR Travels reserves the right to amend at any time. Latest version is always available on this page.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default RefundPolicyPage;
